import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import ReportProblemOutlined from "@material-ui/icons/ReportProblemOutlined";
import { Formik, FormikActions, FormikProps, getIn } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { useMutation } from "react-apollo";
import {
  CampoPendente,
  CampoValorInput,
  SALVAR_ATUALIZACAO_CADASTRAL,
  SalvarAtualizacaoCadastralMutation,
  SalvarAtualizacaoCadastralMutationVariables,
} from "./atualizacaoCadastralGql";
import CampoCidade from "./CampoCidade";
import { definicaoCampo, schemaValidacao, valoresIniciais } from "./camposAtualizacaoCadastral";
import usePreenchimentoCep, { EnderecoPreenchido, MENSAGEM_SITUACAO_CEP } from "./usePreenchimentoCep";
import useSairSistema from "./useSairSistema";

type FormValues = { [campo: string]: string };

interface Props {
  titulo: string;
  mensagem: string;
  campos: CampoPendente[];
  onSalvo: () => void;
}

const ERRO_INESPERADO = "Não foi possível salvar a atualização cadastral. Tente novamente.";

const idCampo = (campo: string) => `atualizacao-cadastral-${campo}`;

const useStyles = makeStyles((theme) => ({
  aviso: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "rgb(255, 244, 229)",
    color: "rgb(102, 60, 0)",
  },
  iconeAviso: {
    marginRight: theme.spacing(1.5),
    color: "#ff9800",
  },
  mensagem: {
    whiteSpace: "pre-line",
  },

  campos: {
    marginTop: 0,
    marginBottom: 0,
    "& > $campo": {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  campo: {},
  erroGeral: {
    marginTop: theme.spacing(2),
  },
}));

interface CamposProps {
  formikBag: FormikProps<FormValues>;
  campos: CampoPendente[];
  desabilitado: boolean;
}

const CamposAtualizacao = ({ formikBag, campos, desabilitado }: CamposProps) => {
  const classes = useStyles();
  const { setFieldValue } = formikBag;
  const pedidos = useMemo(() => new Set(campos.map(({ campo }) => campo)), [campos]);

  const preencher = useCallback(
    (endereco: EnderecoPreenchido) => {
      (Object.keys(endereco) as Array<keyof EnderecoPreenchido>).forEach((campo) => {
        const valor = endereco[campo];
        if (valor && pedidos.has(campo)) {
          setFieldValue(campo, valor.slice(0, definicaoCampo(campo).tamanhoMaximo));
        }
      });
      const numero = document.getElementById(idCampo("NUMERO"));
      if (numero && endereco.ENDERECO) {
        numero.focus();
      }
    },
    [pedidos, setFieldValue]
  );
  const { situacaoCep, aoAlterarCep } = usePreenchimentoCep(preencher);

  return (
    <Grid container spacing={2} className={classes.campos}>
      {campos.map(({ campo, rotulo, obrigatorio }, indice) => {
        const definicao = definicaoCampo(campo);
        const erro = getIn(formikBag.errors, campo);
        const mostrarErro = !!getIn(formikBag.touched, campo) && !!erro;
        const valor = formikBag.values[campo] || "";
        if (definicao.cidadeUf) {
          return (
            <Grid item xs={12} sm={definicao.largura} key={campo} className={classes.campo}>
              <CampoCidade
                id={idCampo(campo)}
                value={valor}
                onChange={(cidade) => setFieldValue(campo, cidade)}
                onBlur={() => formikBag.setFieldTouched(campo, true)}
                erro={mostrarErro ? erro : ""}
                disabled={desabilitado}
                required={obrigatorio !== false}
              />
            </Grid>
          );
        }
        const situacao = campo === "CEP" && situacaoCep ? MENSAGEM_SITUACAO_CEP[situacaoCep] : undefined;
        return (
          <Grid item xs={12} sm={definicao.largura} key={campo} className={classes.campo}>
            <TextField
              id={idCampo(campo)}
              name={campo}
              label={rotulo}
              type={definicao.tipoInput}
              placeholder={definicao.placeholder}
              autoComplete={definicao.autoComplete}
              autoFocus={indice === 0}
              value={valor}
              onChange={(e) => {
                const formatado = definicao.formatar(e.target.value);
                setFieldValue(campo, formatado);
                if (campo === "CEP") {
                  aoAlterarCep(formatado);
                }
              }}
              onBlur={formikBag.handleBlur}
              error={mostrarErro}
              helperText={mostrarErro ? erro : situacao}
              inputProps={{ maxLength: definicao.tamanhoMaximo }}
              disabled={desabilitado}
              InputLabelProps={{ shrink: true }}
              required={obrigatorio !== false}
              fullWidth
            />
          </Grid>
        );
      })}
    </Grid>
  );
};


const AtualizacaoCadastralDialog = ({ titulo, mensagem, campos, onSalvo }: Props) => {
  const classes = useStyles();
  const [erroGeral, setErroGeral] = useState("");
  const { sair, saindo } = useSairSistema();
  const [salvar] = useMutation<SalvarAtualizacaoCadastralMutation, SalvarAtualizacaoCadastralMutationVariables>(
    SALVAR_ATUALIZACAO_CADASTRAL
  );

  const initialValues = useMemo(() => valoresIniciais(campos), [campos]);
  const validationSchema = useMemo(() => schemaValidacao(campos), [campos]);

  const submit = async (values: FormValues, actions: FormikActions<FormValues>) => {
    setErroGeral("");
    const valores: CampoValorInput[] = campos.map(({ campo }) => ({ campo, valor: (values[campo] || "").trim() }));
    try {
      const response = await salvar({ variables: { valores } });
      const resultado = response && response.data ? response.data.salvarAtualizacaoCadastral : null;
      if (resultado && resultado.sucesso) {
        onSalvo();
        return;
      }
      const erros = (resultado && resultado.erros) || [];
      const gerais: string[] = [];
      erros.forEach(({ campo, mensagem }) => {
        if (campo && campos.some((c) => c.campo === campo)) {
          actions.setFieldError(campo, mensagem);
          actions.setFieldTouched(campo, true, false);
        } else {
          gerais.push(mensagem);
        }
      });
      setErroGeral(gerais.length > 0 ? gerais.join(" ") : erros.length > 0 ? "" : ERRO_INESPERADO);
    } catch (e) {
      setErroGeral(ERRO_INESPERADO);
    }
    actions.setSubmitting(false);
  };

  return (
    <Dialog open disableEscapeKeyDown maxWidth="sm" fullWidth aria-labelledby="atualizacao-cadastral-titulo">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={submit}
        render={(formikBag: FormikProps<FormValues>) => {
          const ocupado = formikBag.isSubmitting || saindo;
          return (
            <form noValidate onSubmit={formikBag.handleSubmit}>
              <DialogTitle id="atualizacao-cadastral-titulo">{titulo}</DialogTitle>
              <DialogContent>
                {mensagem && <DialogContentText className={classes.mensagem}>{mensagem}</DialogContentText>}
                <CamposAtualizacao formikBag={formikBag} campos={campos} desabilitado={ocupado} />
                <div className={classes.aviso} role="alert">
                  <ReportProblemOutlined className={classes.iconeAviso} />
                  <Typography variant="body2">
                    Para continuar o acesso, é necessário atualizar seus dados cadastrais.
                  </Typography>
                </div>
                {erroGeral && (
                  <Typography color="error" variant="body2" className={classes.erroGeral}>
                    {erroGeral}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button color="secondary" onClick={sair} disabled={ocupado}>
                  Sair
                </Button>
                <Button color="primary" variant="contained" type="submit" disabled={ocupado}>
                  Salvar
                </Button>
              </DialogActions>
              {ocupado && <LinearProgress color="secondary" style={{ height: 5.5 }} />}
            </form>
          );
        }}
      />
    </Dialog>
  );
};

export default AtualizacaoCadastralDialog;
